// The combat look registry: one World theme look id in, one recipe out
// (R1007, `ui-map-interface.md` §6.3.3).
//
// Six fields of a World body name a gun look: `shotMelee`, `shotArtillery`
// and `shotSentinel` are the rounds one salvo flies, and `hitShield`,
// `hitHull` and `hitSink` are what one round draws when it arrives. The
// player picks a look per field; this module turns the id into the draw code
// and the figure row, and decides nothing else.
//
// **The arithmetic is not here.** `combat-recipe-kit.ts` holds the row and
// context types, the eases, the hash, the alpha clamps, the atlas cell keys
// and every primitive. This module re-exports the kit, so a reader and an
// importer take one name from one place.
//
// **A look resolves through its own recipe file first.** The owner picked ten
// cards per mockup round 048 to 053, and the draw code of a picked card lives
// in `combat-recipes-{melee,artillery,sentinel,shield,hull,sink}.ts`, keyed by
// the look id. A look that file lists in its `PENDING` table resolves to the
// AUTO look of its list — the recipe AND the figure row — so every id a player
// can pick draws something from the day its data lands, and the recipes arrive
// one round at a time. `<file>.test.ts` refuses an id that is neither
// implemented nor pending, so a card cannot be forgotten in silence.
//
// This is a leaf (`leaves.test.ts`).

import {
  HIT_HULL_STYLES, HIT_SHIELD_STYLES, HIT_SINK_STYLES,
  SHOT_ARTILLERY_STYLES, SHOT_MELEE_STYLES, SHOT_SENTINEL_STYLES,
} from '../gen/balance';
import { COMBAT_HITS, COMBAT_SHOTS } from '../gen/presentation';
import type { CombatHitRow, CombatShotRow } from '../gen/presentation';
import type { HitRecipeFn, ShotRecipeFn } from './combat-recipe-kit';
import { RECIPES as MELEE, PENDING as MELEE_PENDING } from './combat-recipes-melee';
import {
  RECIPES as ARTILLERY, PENDING as ARTILLERY_PENDING,
} from './combat-recipes-artillery';
import {
  RECIPES as SENTINEL, PENDING as SENTINEL_PENDING,
} from './combat-recipes-sentinel';
import {
  RECIPES as SHIELD, PENDING as SHIELD_PENDING, OWNS_HULL as SHIELD_OWNS,
} from './combat-recipes-shield';
import {
  RECIPES as HULL, PENDING as HULL_PENDING, OWNS_HULL as HULL_OWNS,
} from './combat-recipes-hull';
import {
  RECIPES as SINK, PENDING as SINK_PENDING, OWNS_HULL as SINK_OWNS,
} from './combat-recipes-sink';
import { buildLooks, type Look, type RecipeFile } from './recipe-kit';

export * from './combat-recipe-kit';

/** The three World fields that name a shot look. */
export const SHOT_FIELDS = ['shotMelee', 'shotArtillery', 'shotSentinel'] as const;
export type ShotField = (typeof SHOT_FIELDS)[number];

/** The three World fields that name a hit look. */
export const HIT_FIELDS = ['hitShield', 'hitHull', 'hitSink'] as const;
export type HitField = (typeof HIT_FIELDS)[number];

/** One resolved shot look: the id asked for, its figures and its draw code. */
export type ShotLook = Look<CombatShotRow, ShotRecipeFn>;

/** One resolved hit look. */
export interface HitLook extends Look<CombatHitRow, HitRecipeFn> {
  /** True where the look draws the victim's hull itself, so the layer takes
   *  the fleet layer's own hull instance away for the life of the mark. */
  readonly ownsHull: boolean;
}

const SHOT_FILES: Readonly<Record<ShotField, RecipeFile<ShotRecipeFn>>> = {
  shotMelee: { recipes: MELEE, pending: MELEE_PENDING },
  shotArtillery: { recipes: ARTILLERY, pending: ARTILLERY_PENDING },
  shotSentinel: { recipes: SENTINEL, pending: SENTINEL_PENDING },
};

const HIT_FILES: Readonly<Record<HitField, RecipeFile<HitRecipeFn> & {
  ownsHull: readonly string[];
}>> = {
  hitShield: { recipes: SHIELD, pending: SHIELD_PENDING, ownsHull: SHIELD_OWNS },
  hitHull: { recipes: HULL, pending: HULL_PENDING, ownsHull: HULL_OWNS },
  hitSink: { recipes: SINK, pending: SINK_PENDING, ownsHull: SINK_OWNS },
};

/** The six files by field, for the test that reads a field's halves back. */
const FILES: Readonly<Record<ShotField | HitField, RecipeFile<unknown>>> = {
  ...SHOT_FILES, ...HIT_FILES,
};

/** The id list of each field, in cycle order. Position 0 is the AUTO look. */
const SHOT_IDS: Readonly<Record<ShotField, readonly string[]>> = {
  shotMelee: SHOT_MELEE_STYLES,
  shotArtillery: SHOT_ARTILLERY_STYLES,
  shotSentinel: SHOT_SENTINEL_STYLES,
};

const HIT_IDS: Readonly<Record<HitField, readonly string[]>> = {
  hitShield: HIT_SHIELD_STYLES,
  hitHull: HIT_HULL_STYLES,
  hitSink: HIT_SINK_STYLES,
};

const IDS: Readonly<Record<ShotField | HitField, readonly string[]>> = {
  ...SHOT_IDS, ...HIT_IDS,
};

/**
 * Every look of every field, resolved once through the shared `buildLooks`,
 * so a frame looks a pick up by field and id and does no work.
 */
function buildShots(): Readonly<Record<ShotField, Readonly<Record<string, ShotLook>>>> {
  const out = {} as Record<ShotField, Readonly<Record<string, ShotLook>>>;
  for (const field of SHOT_FIELDS) {
    out[field] = buildLooks(SHOT_IDS[field], SHOT_FILES[field].recipes, COMBAT_SHOTS[field]);
  }
  return out;
}

function buildHits(): Readonly<Record<HitField, Readonly<Record<string, HitLook>>>> {
  const out = {} as Record<HitField, Record<string, HitLook>>;
  for (const field of HIT_FIELDS) {
    const half = HIT_FILES[field];
    const table: Record<string, HitLook> = {};
    for (const look of Object.values(buildLooks(HIT_IDS[field], half.recipes, COMBAT_HITS[field]))) {
      // The look that DRAWS decides, because it is the one that puts a hull
      // on the water.
      table[look.id] = { ...look, ownsHull: half.ownsHull.includes(look.drawn) };
    }
    out[field] = table;
  }
  return out;
}

const SHOT_LOOKS = buildShots();
const HIT_LOOKS = buildHits();

/** The shot look of one field, or the field's AUTO look for an unknown id. */
export function shotLook(field: ShotField, id: string): ShotLook {
  return SHOT_LOOKS[field][id] ?? SHOT_LOOKS[field][SHOT_IDS[field][0]];
}

/** The hit look of one field, or the field's AUTO look for an unknown id. */
export function hitLook(field: HitField, id: string): HitLook {
  return HIT_LOOKS[field][id] ?? HIT_LOOKS[field][HIT_IDS[field][0]];
}

/**
 * Which ids of one field a recipe file has built, and which it left pending.
 *
 * The test of each recipe file reads this back, so an id that is in neither
 * table fails the gate rather than resolving to the default in silence.
 */
export function fileHalfOf(field: ShotField | HitField): {
  recipes: readonly string[]; pending: readonly string[];
} {
  const half = FILES[field];
  return { recipes: Object.keys(half.recipes), pending: half.pending };
}

/** The id list of one field, whichever half it belongs to. */
export function idsOf(field: ShotField | HitField): readonly string[] {
  return IDS[field];
}
